import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Zap, Play, Plus, Trash2, Loader2, Check, X, Bot } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { PageHeader } from "@/components/app/PageHeader";
import { Loader, EmptyState } from "@/components/app/Loader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const TOPICS = ["School Funding", "Public Safety", "Roads & Infrastructure", "Property Taxes", "Healthcare", "Jobs & Economy", "Water Quality"];
const ACTIONS = { draft_social: "Draft social", draft_email: "Draft email", notify_manager: "Notify manager" };

export default function Automations() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("admin", "manager");
  const qc = useQueryClient();

  const { data: triggers, isLoading } = useQuery({ queryKey: ["triggers"], queryFn: () => api.get("/triggers").then((r) => r.data) });
  const { data: queue } = useQuery({ queryKey: ["reviewQueue"], queryFn: () => api.get("/triggers/review-queue").then((r) => r.data) });

  const toggle = useMutation({ mutationFn: (id) => api.patch(`/triggers/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["triggers"] }) });
  const del = useMutation({ mutationFn: (id) => api.delete(`/triggers/${id}`), onSuccess: () => { toast.success("Trigger removed"); qc.invalidateQueries({ queryKey: ["triggers"] }); } });
  const run = useMutation({
    mutationFn: () => api.post("/triggers/run").then((r) => r.data),
    onSuccess: (d) => { toast.success(`${d.drafted} draft(s), ${d.notifications} notification(s)`); qc.invalidateQueries({ queryKey: ["reviewQueue"] }); qc.invalidateQueries({ queryKey: ["feed"] }); },
    onError: (e) => toast.error(apiError(e)),
  });
  const review = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/content/drafts/${id}`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["reviewQueue"] }); toast.success("Updated"); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div>
      <PageHeader
        title="Automations"
        subtitle="Condition → action rules. Automated comms always DRAFT into a review queue. They never auto-send."
        icon={Zap}
        actions={
          canEdit && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => run.mutate()} disabled={run.isPending} data-testid="run-triggers-btn">
                {run.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />} Run now
              </Button>
              <TriggerDialog />
            </div>
          )
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Triggers */}
        <div>
          <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wider text-brand-mute">Rules</h3>
          {isLoading ? <Loader /> : !triggers?.length ? <EmptyState icon={Zap} title="No triggers" description="Create your first automation rule." /> : (
            <div className="space-y-3">
              {triggers.map((t) => (
                <div key={t.id} className="rounded-xl border border-border bg-white p-4 shadow-sm" data-testid="trigger-card">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-heading font-bold text-brand-ink">{t.name}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                        {t.condition?.topic && <Badge variant="outline">{t.condition.topic}</Badge>}
                        {t.condition?.sentiment && <Badge variant="outline" className="capitalize">{t.condition.sentiment}</Badge>}
                        <span className="text-brand-mute">→</span>
                        <Badge className="bg-brand-red/10 text-brand-red">{ACTIONS[t.action] || t.action}</Badge>
                        {t.proposed && <Badge className="bg-neu-bg text-neu-text">proposed</Badge>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={!!t.enabled} disabled={!canEdit} onCheckedChange={() => toggle.mutate(t.id)} data-testid={`toggle-trigger-${t.id}`} />
                      {canEdit && <Button variant="ghost" size="icon" onClick={() => del.mutate(t.id)}><Trash2 className="h-4 w-4 text-brand-red" /></Button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Review queue */}
        <div>
          <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wider text-brand-mute">Review queue</h3>
          {!queue?.length ? (
            <EmptyState icon={Bot} title="Queue is clear" description="Run your triggers to generate rapid-response drafts for approval." />
          ) : (
            <div className="space-y-3">
              {queue.map((d) => (
                <div key={d.id} className="rounded-xl border border-border bg-white p-4 shadow-sm" data-testid="review-card">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="capitalize">{d.type.replace("_", " ")}</Badge>
                    <span className="text-xs text-brand-mute">{d.trigger_name} · {timeAgo(d.created_at)}</span>
                  </div>
                  {d.source_signal_title && <p className="mt-2 text-xs font-medium text-brand-navy">re: {d.source_signal_title}</p>}
                  <pre className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap font-body text-sm text-brand-ink">{d.variants?.[0]?.body}</pre>
                  <Link to={`/studio?draft=${d.id}`} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-blue hover:underline" data-testid={`open-draft-in-studio-${d.id}`}>
                    Open in Content Studio →
                  </Link>
                  {canEdit && (
                    <div className="mt-3 flex gap-2 border-t border-border pt-3">
                      <Button size="sm" onClick={() => review.mutate({ id: d.id, status: "published" })} className="bg-pos-text text-white hover:opacity-90" data-testid={`approve-review-${d.id}`}><Check className="mr-1.5 h-3.5 w-3.5" /> Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => review.mutate({ id: d.id, status: "rejected" })} data-testid={`reject-review-${d.id}`}><X className="mr-1.5 h-3.5 w-3.5" /> Reject</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TriggerDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [topic, setTopic] = useState(TOPICS[0]);
  const [sentiment, setSentiment] = useState("negative");
  const [action, setAction] = useState("draft_social");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) return toast.error("Name your trigger");
    setBusy(true);
    try {
      await api.post("/triggers", { name, condition: { topic, sentiment }, action, approval_required: true, enabled: true });
      toast.success("Trigger created");
      qc.invalidateQueries({ queryKey: ["triggers"] });
      setOpen(false); setName("");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-brand-red text-white hover:bg-brand-redhover" data-testid="new-trigger-btn"><Plus className="mr-2 h-4 w-4" /> New rule</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">New automation rule</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Negative property-tax rapid response" className="mt-1.5" data-testid="trigger-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>When topic</Label>
              <Select value={topic} onValueChange={setTopic}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{TOPICS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>And sentiment</Label>
              <Select value={sentiment} onValueChange={setSentiment}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{["positive", "neutral", "negative"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <div><Label>Then</Label>
            <Select value={action} onValueChange={setAction}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(ACTIONS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} className="bg-brand-red text-white hover:bg-brand-redhover" data-testid="save-trigger-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create rule"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
